import { useMemo, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { ChevronDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { classifyCampaign } from '@/hooks/usePerformanceData';
import { cn } from '@/lib/utils';
import { todayISO } from '@/lib/performance/date-window';
import type { PlatformScope } from '@/lib/performance/types';
import {
  campaignsDataAtom,
  consolidatedPerformanceScopeAtom,
  consolidatedPerformanceDateRangeAtom,
  consolidatedPerformanceCustomRangeAtom,
  consolidatedPerformanceGroupByAtom,
  consolidatedPerformanceComparisonAtom,
  consolidatedPerformanceCampaignTypesAtom,
  consolidatedPerformanceCampaignIdsAtom,
  consolidatedPerformanceIncludeExternalAtom,
} from '@/store';

// NOTE: these option sets are duplicated from performance-v2.tsx for now;
// #5994 tracks extracting them into a shared module so both surfaces import one copy.
const OVERVIEW_DATE_OPTIONS = [
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' },
  { value: 180, label: '180 days' },
  { value: 365, label: '365 days' },
  { value: 0, label: 'All time' },
];

const GROUP_BY_OPTIONS = [
  { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
];

// Shift-mode comparisons move the SAME span back by a unit (#4309); a shift
// shorter than the window overlaps it and the deltas compare a period with
// itself. Offer only shifts of at least the window length (ruling 09-08:
// comparisons are always against a full previous span of the same length).
const COMPARISON_SHIFT_DAYS: Record<string, number> = {
  'previous-week': 7,
  'previous-month': 28,
  'previous-year': 365,
};

const getComparisonOptions = (days: number) => {
  const options = [
    { value: 'none', label: 'No comparison' },
    { value: 'previous', label: days > 0 ? `vs Previous ${days} days` : 'vs Previous period' },
  ];
  if (days > 0 && days <= COMPARISON_SHIFT_DAYS['previous-week']) options.push({ value: 'previous-week', label: 'vs Previous week' });
  if (days > 0 && days <= COMPARISON_SHIFT_DAYS['previous-month']) options.push({ value: 'previous-month', label: 'vs Previous month' });
  if (days > 0 && days <= COMPARISON_SHIFT_DAYS['previous-year']) options.push({ value: 'previous-year', label: 'vs Previous year' });
  return options;
};

const CAMPAIGN_TYPE_TABS = [
  { value: 'all', label: 'All' },
  { value: 'brand', label: 'Brand' },
  { value: 'prospecting', label: 'Prosp.' },
  { value: 'competitor', label: 'Comp.' },
  { value: 'icp', label: 'ICP' },
  { value: 'hipo', label: 'HiPo' },
  { value: 'location', label: 'Geo' },
];

const SCOPE_OPTIONS: { value: PlatformScope; label: string; logo?: string }[] = [
  { value: 'all', label: 'All platforms' },
  { value: 'google', label: 'Google', logo: '/google-ads.svg' },
  { value: 'meta', label: 'Meta', logo: '/meta.png' },
];

// Tabs whose content renders comparison deltas (#6276): Overview metric cards,
// Campaigns by-campaign cells, Creative & Ads' Ads sub-view. Everywhere else
// the section is disabled instead of silently doing nothing.
const COMPARISON_TABS = new Set(['overview', 'campaigns', 'creative-ads']);

const chipClass = (active: boolean) =>
  cn(
    'px-3 py-1 rounded-full text-xs font-medium border transition-colors',
    active ? 'bg-[#290BD9] border-[#290BD9] text-white' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50',
  );

/**
 * The consolidated Performance filters (#6500), two fixed-shape controls in
 * the tab row (ruling 2026-09-08 pm: platform & campaigns deserve their own
 * dropdown — one condensed popover meant too much scrolling):
 *
 * 1. Platform & campaigns — scope chips plus the Google campaign narrowing
 *    (type chips, external toggle, campaign list). Trigger: scope label +
 *    always-on campaign-count bubble (#6502).
 * 2. Filters — the window set: date presets, the custom range picker,
 *    grouping and comparison. Trigger: funnel + active-narrowing bubble
 *    (the Meetings-library idiom).
 */
export function ConsolidatedFilters({ scope, activeTab }: { scope: PlatformScope; activeTab: string }) {
  const [, setScope] = useAtom(consolidatedPerformanceScopeAtom);
  const googleInScope = scope !== 'meta';
  const comparisonUsable = googleInScope && COMPARISON_TABS.has(activeTab);
  // Group By aggregates the Overview's performance-over-time chart; the table
  // tabs have no time axis (#6276).
  const groupByUsable = activeTab === 'overview';

  const [dateRange, setDateRange] = useAtom(consolidatedPerformanceDateRangeAtom);
  const [customRange, setCustomRange] = useAtom(consolidatedPerformanceCustomRangeAtom);
  const [groupBy, setGroupBy] = useAtom(consolidatedPerformanceGroupByAtom);
  const [comparison, setComparison] = useAtom(consolidatedPerformanceComparisonAtom);
  const [campaignTypes, setCampaignTypes] = useAtom(consolidatedPerformanceCampaignTypesAtom);
  const [selectedCampaignIds, setSelectedCampaignIds] = useAtom(consolidatedPerformanceCampaignIdsAtom);
  const [includeExternal, setIncludeExternal] = useAtom(consolidatedPerformanceIncludeExternalAtom);

  const allCampaigns = useAtomValue(campaignsDataAtom);
  // #6128 — the campaign picker lists every campaign the surface shows, in any state; the old
  // LIVE/PAUSED filter was the same accidental narrowing fixed in usePerformanceData.
  const activeCampaigns = allCampaigns;
  // #6502 — only offer type chips that can match something: a chip for a type
  // with zero campaigns in this organization is a dead option.
  const presentTypes = useMemo(() => new Set(activeCampaigns.map((c) => classifyCampaign(c))), [activeCampaigns]);
  const typeTabs = useMemo(
    () => CAMPAIGN_TYPE_TABS.filter((t) => t.value === 'all' || presentTypes.has(t.value)),
    [presentTypes],
  );
  const typeFilter = useMemo<(type: string) => boolean>(() => {
    if (campaignTypes.size === 0 || campaignTypes.has('all')) return () => true;
    return (type: string) => campaignTypes.has(type);
  }, [campaignTypes]);
  const filteredCampaigns = useMemo(
    () => activeCampaigns.filter((c) => typeFilter(classifyCampaign(c))),
    [activeCampaigns, typeFilter],
  );

  const toggleCampaignType = (value: string) =>
    setCampaignTypes((prev) => {
      if (value === 'all') return new Set(['all']);
      const next = new Set(prev);
      next.delete('all');
      if (next.has(value)) next.delete(value);
      else next.add(value);
      if (next.size === 0) next.add('all');
      return next;
    });

  // Control 1's bubble counts ACTIVE narrowings (ruling 2026-09-08: a
  // campaign tally read as a wrong filter count) — absent at defaults, while
  // the trigger keeps its fixed name and width (#6502).
  const campaignFilterCount =
    (scope !== 'all' ? 1 : 0) +
    (!campaignTypes.has('all') && campaignTypes.size > 0 ? campaignTypes.size : 0) +
    (selectedCampaignIds.size > 0 ? 1 : 0) +
    (includeExternal ? 1 : 0);
  // Control 2's trigger shows ONLY the range ('Custom' when the picker is in
  // use); everything else active inside (comparison, non-default grouping)
  // counts in a bubble (ruling 2026-09-08).
  const rangeLabel = customRange
    ? 'Custom'
    : (OVERVIEW_DATE_OPTIONS.find((d) => d.value === dateRange)?.label ?? 'All time');
  const extraWindowCount = (comparisonUsable && comparison !== 'none' ? 1 : 0) + (groupBy !== 'day' ? 1 : 0);

  // Group-by prunes to groupings with enough buckets to read (weekly from 28
  // days, monthly from 90; All time = 0 offers everything) — the same
  // dead-option rule as the type chips. One surviving option hides the
  // section, and window changes clamp a now-unreadable grouping back to day.
  const spanDays = (custom: { start: string; end: string } | null, days: number) =>
    custom ? Math.round((Date.parse(custom.end) - Date.parse(custom.start)) / 86_400_000) + 1 : days;
  const windowDays = spanDays(customRange, dateRange);
  const groupByOptions = GROUP_BY_OPTIONS.filter(
    (opt) =>
      opt.value === 'day' ||
      (opt.value === 'week' && (windowDays === 0 || windowDays >= 28)) ||
      (opt.value === 'month' && (windowDays === 0 || windowDays >= 90)),
  );
  const applyWindow = (days: number, custom: { start: string; end: string } | null) => {
    setCustomRange(custom);
    if (custom === null) setDateRange(days);
    const span = spanDays(custom, days);
    if (span !== 0 && ((groupBy === 'week' && span < 28) || (groupBy === 'month' && span < 90))) {
      setGroupBy('day');
    }
    // A shift comparison that the new window would overlap resets to none.
    const shift = COMPARISON_SHIFT_DAYS[comparison];
    if (shift !== undefined && (span === 0 || span > shift)) setComparison('none');
  };

  // Draft state for the custom picker; committed on Apply so half-typed
  // ranges never hit the queries.
  const [draftStart, setDraftStart] = useState('');
  const [draftEnd, setDraftEnd] = useState('');
  // Tell-on-click (house rule): Apply is never silently disabled — a click
  // with an invalid draft names the problem here and clears on edit.
  const [rangeError, setRangeError] = useState<string | null>(null);

  // "All Campaigns" doubles as deselect-all (ruling 2026-09-08): from the
  // everything-selected state one click clears every row so the next pick
  // selects exactly one campaign. UI-only — the atom's empty set still means
  // "all", so an abandoned popover falls back to all rows.
  const [noneSelected, setNoneSelected] = useState(false);

  return (
    <div className="flex items-center gap-2">
      {/* Platform & campaigns */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="h-9 gap-2 text-gray-900">
            <span>Campaigns</span>
            {/* The bubble slot is always reserved (blank at zero) so the
                trigger keeps one width as narrowings toggle, without the
                old fixed w-44 dead space. */}
            <span
              className={`flex h-5 min-w-5 flex-shrink-0 items-center justify-center rounded-full px-1 text-xs font-medium ${
                campaignFilterCount > 0 ? 'bg-[#290BD9] text-white' : ''
              }`}
            >
              {campaignFilterCount > 0 ? campaignFilterCount : ''}
            </span>
            <ChevronDown className="h-4 w-4 flex-shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-0">
        <div className="p-3">
          <p className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500">Platform</p>
          <div className="flex gap-1.5">
            {SCOPE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setScope(opt.value)}
                className={cn(chipClass(scope === opt.value), 'flex items-center gap-1.5')}
              >
                {opt.logo && <img src={opt.logo} alt="" className="h-3.5 w-3.5 object-contain" />}
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <Separator />
        <div
          className={cn('p-3', !googleInScope && 'pointer-events-none opacity-40')}
          title={googleInScope ? undefined : 'Campaign narrowing applies to Google campaigns.'}
        >
          <p className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500">Campaign type</p>
          <div className="flex flex-wrap gap-1.5">
            {typeTabs.map((t) => {
              const isAll = t.value === 'all';
              const isActive = isAll ? campaignTypes.has('all') || campaignTypes.size === 0 : campaignTypes.has(t.value);
              return (
                <button key={t.value} type="button" onClick={() => toggleCampaignType(t.value)} className={chipClass(isActive)}>
                  {t.label}
                </button>
              );
            })}
            {/* External is orthogonal to campaign type (a campaign is Brand/Prosp/Comp AND
                managed-or-not), so it reads as an additive include/exclude, set apart by a divider. */}
            <span className="mx-0.5 w-px self-stretch bg-gray-200" aria-hidden />
            <button
              type="button"
              onClick={() => setIncludeExternal(!includeExternal)}
              title="Include external campaigns not managed by Glitch"
              className={chipClass(includeExternal)}
            >
              External
            </button>
          </div>
          <p className="mb-1 mt-3 text-xs font-medium uppercase tracking-wider text-gray-500">Campaigns</p>
          {/* Fixed height: toggling type chips changes the row count, and a
              resizing popover jumps under the cursor (ruling 2026-09-08). */}
          <div className="h-[160px] overflow-y-auto">
            {filteredCampaigns.length === 0 ? (
              <div className="flex h-full items-center justify-center px-3 text-center text-xs text-gray-400">
                No campaigns available for this filter
              </div>
            ) : (
              <>
                <div
                  className="flex cursor-pointer items-center space-x-2 rounded-sm px-2 py-1.5 hover:bg-gray-100"
                  onClick={() => {
                    const effectivelyAll =
                      !noneSelected &&
                      (selectedCampaignIds.size === 0 || filteredCampaigns.every((c) => selectedCampaignIds.has(c.id)));
                    setSelectedCampaignIds(new Set());
                    setNoneSelected(effectivelyAll);
                  }}
                >
                  <Checkbox
                    checked={
                      !noneSelected &&
                      (selectedCampaignIds.size === 0 || filteredCampaigns.every((c) => selectedCampaignIds.has(c.id)))
                    }
                    className="pointer-events-none data-[state=checked]:bg-[#290BD9] data-[state=checked]:border-[#290BD9]"
                  />
                  <span className="text-sm font-medium">All Campaigns</span>
                </div>
                {filteredCampaigns.map((c) => {
                  const isSelected = !noneSelected && (selectedCampaignIds.size === 0 || selectedCampaignIds.has(c.id));
                  return (
                    <div
                      key={c.id}
                      className="flex cursor-pointer items-center space-x-2 rounded-sm px-2 py-1.5 hover:bg-gray-100"
                      onClick={() => {
                        if (noneSelected) {
                          // First pick after deselect-all: exactly this one.
                          setNoneSelected(false);
                          setSelectedCampaignIds(new Set([c.id]));
                          return;
                        }
                        setSelectedCampaignIds((prev) => {
                          const next = new Set(prev.size === 0 ? filteredCampaigns.map((fc) => fc.id) : prev);
                          if (next.has(c.id)) next.delete(c.id);
                          else next.add(c.id);
                          // Back to everything selected → reset to empty (means "all").
                          if (filteredCampaigns.every((fc) => next.has(fc.id))) return new Set();
                          return next;
                        });
                      }}
                    >
                      <Checkbox checked={isSelected} className="data-[state=checked]:bg-[#290BD9] data-[state=checked]:border-[#290BD9]" />
                      <span className="truncate text-sm">{c.name}</span>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
        </PopoverContent>
      </Popover>

      {/* Filters: the window set */}
      <Popover
        onOpenChange={(open) => {
          if (open) {
            setDraftStart(customRange?.start ?? '');
            setDraftEnd(customRange?.end ?? '');
          }
        }}
      >
        <PopoverTrigger asChild>
          <Button variant="outline" className="h-9 w-32 justify-between text-gray-900">
            <span className="flex min-w-0 items-center gap-2">
              <span className="truncate">{rangeLabel}</span>
              {extraWindowCount > 0 && (
                <span className="flex h-5 min-w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#290BD9] px-1 text-xs font-medium text-white">
                  {extraWindowCount}
                </span>
              )}
            </span>
            <ChevronDown className="ml-2 h-4 w-4 flex-shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-0">
        <div className="p-3">
          <p className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500">Date range</p>
          <div className="flex flex-wrap gap-1.5">
            {OVERVIEW_DATE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => applyWindow(opt.value, null)}
                className={chipClass(!customRange && dateRange === opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {/* Both pickers clamp to today; performance data cannot exist in the
              future. The Apply guard repeats the bound because typed dates
              bypass the native picker's max. */}
          <div className="mt-2 flex items-end gap-2">
            <label className="block flex-1 text-xs text-gray-500">
              From
              <input
                type="date"
                value={draftStart}
                max={draftEnd && draftEnd < todayISO() ? draftEnd : todayISO()}
                onChange={(e) => {
                  setDraftStart(e.target.value);
                  setRangeError(null);
                }}
                className={cn(
                  'mt-0.5 w-full rounded-md border px-2 py-1 text-sm text-gray-900 focus:border-[#290BD9] focus:outline-none',
                  rangeError ? 'border-[#FCA5A5]' : 'border-gray-300',
                )}
              />
            </label>
            <label className="block flex-1 text-xs text-gray-500">
              To
              <input
                type="date"
                value={draftEnd}
                min={draftStart || undefined}
                max={todayISO()}
                onChange={(e) => {
                  setDraftEnd(e.target.value);
                  setRangeError(null);
                }}
                className={cn(
                  'mt-0.5 w-full rounded-md border px-2 py-1 text-sm text-gray-900 focus:border-[#290BD9] focus:outline-none',
                  rangeError ? 'border-[#FCA5A5]' : 'border-gray-300',
                )}
              />
            </label>
            <Button
              variant="outline"
              className="h-[30px] px-3 text-gray-900"
              onClick={() => {
                if (!draftStart || !draftEnd) {
                  setRangeError('Pick both dates.');
                  return;
                }
                if (draftStart > draftEnd) {
                  setRangeError('The start date must be before the end date.');
                  return;
                }
                if (draftEnd > todayISO()) {
                  setRangeError('The end date cannot be in the future.');
                  return;
                }
                setRangeError(null);
                applyWindow(dateRange, { start: draftStart, end: draftEnd });
              }}
            >
              Apply
            </Button>
          </div>
          {rangeError && <p className="mt-1 text-xs text-[#B91C1C]">{rangeError}</p>}
          {customRange && (
            <button
              type="button"
              className="mt-1.5 text-xs text-[#290BD9] hover:text-[#2309C5] hover:underline"
              onClick={() => applyWindow(dateRange, null)}
            >
              Clear custom range ({customRange.start} to {customRange.end})
            </button>
          )}
        </div>
        {groupByOptions.length > 1 && (
          <>
            <Separator />
            <div
              className={cn('p-3', !groupByUsable && 'opacity-40')}
              title={groupByUsable ? undefined : 'Grouping applies to the Overview chart.'}
            >
              <p className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500">Group by</p>
              <div className="flex gap-1.5">
                {groupByOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => groupByUsable && setGroupBy(opt.value as 'day' | 'week' | 'month')}
                    className={cn(chipClass(groupBy === opt.value), !groupByUsable && 'cursor-not-allowed')}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
        <Separator />
        <div
          className={cn('p-3', !comparisonUsable && 'opacity-40')}
          title={comparisonUsable ? undefined : 'Comparison applies to the Overview, Campaigns, and Creative & Ads tabs.'}
        >
          <p className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500">Comparison</p>
          {getComparisonOptions(windowDays).map((opt) => (
            <div
              key={opt.value}
              className={cn(
                'flex items-center space-x-2 rounded-sm px-2 py-1.5',
                comparisonUsable ? 'cursor-pointer hover:bg-gray-100' : 'cursor-not-allowed',
              )}
              onClick={() => comparisonUsable && setComparison(opt.value)}
            >
              <Checkbox checked={comparison === opt.value} className="pointer-events-none" />
              <span className="text-sm">{opt.label}</span>
            </div>
          ))}
        </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
